
import { v4 as uuidv4 } from "uuid";
import React, { useState, useEffect, useRef, useCallback } from "react";

import { 
  Character,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import DisplayObject from "@dndComponents/display/DisplayObject";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterSheetParent from "@dndComponents/character/CharacterSheetParent";
import LoadingBox from "@components/input/LoadingBox";
import FlexBox from "@components/layout/FlexBox";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import ShareButton from "@components/input/buttons/icon_buttons/ShareButton";
import CommonUtilities from "@utilities/common_utilities";
import ToastBox from "@components/display/ToastBox";
import CopyButton from "@components/input/buttons/icon_buttons/CopyButton";
import ArchiveButton from "@components/input/buttons/icon_buttons/ArchiveButton";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  char: Character;
  onUpdated: () => void;
}

export default function CharacterInput({ char, onUpdated }: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [ping, setPing] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [toast_time, setToastTime] = useState(-1);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = useCallback( async () => {
    // console.log(c_char);
    // if (!c_char.char || !c_char.char.owner) {
    //   await api.loadCChars({ campaign, include_players: true, include_chars: true });
    // }
    setLoading(false);
  }, [char]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  const updateCharacter = async (updates?: Partial<Character>) => {
    const user = api.user;
    // debugger;
    if (user && updates) {
      setProcessing(true);
      const res = await char_util.getCharacter({ id: char._id });
      if (res) {
        Object.assign(res, updates);
        await api.updateCharacter(res, ["main"], true);
        const res2 = await char_util.getCharacterFromCookie(char._id);
        const cookie_char = res2;
        if (cookie_char !== null) {
          Object.assign(cookie_char, updates);
          await api.updateCharacter(cookie_char, ["main"]);
        }
        char.copy(res);
      }
      setProcessing(false);
    }
  };

  const copyCharacter = async () => {
    const user = api.user;
    if (user) {
      setProcessing(true);
      const res = await char_util.getCharacter({ id: char._id });
      if (res) {
        const new_obj = res.clone(true);
        new_obj.name = new_obj.name + " (Copy)";
        new_obj._id = "";
        new_obj.owner_id = user._id;
        const res2 = await api.createCharacter(new_obj);
        if (res2.body && !res2.body.error) {
          // Add it to the user's characters, and then update the CharactersInput
          user.my_characters.push(new_obj);
          onUpdated();
        }
      }
      setProcessing(false);
    }
  }

  const deleteCharacter = async () => {
    const user = api.user;
    if (user) {
      setProcessing(true);
      await api.deleteObject("character", char);
      api.removeCharacterCookies(char._id);
      setProcessing(false);
    }
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={viewing}
        width={ window.innerWidth * 0.9 }
        onClose={() => { setViewing(false); }}>
        <CharacterSheetParent
          id={char._id}
          char={char}
        />
      </ModalOrDrawer>
    );
  }
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
    });
  });

  const { classes, cx } = useStyles();

  const renderViewButton = () => {
    return (
      <>
        <FlexBox className="centered">
          <DandDButtonBox
            name="View" 
            disabled={ processing } 
            link_url={`/character/${char._id}`}
            log_it
            onClick={() => {
              setViewing(true);
            }}
          />
        </FlexBox>
      </>
    );
  } 

  const renderShareButton = () => {
    return (
      <>
        <FlexBox className="centered">
          <ShareButton
            no_fab
            disabled={processing}
            onClick={ async () => {
              try {
                if (char.shared_id === "") {
                  char.shared_id = uuidv4().toString();
                  await updateCharacter({ shared_id: char.shared_id });
                  navigator.clipboard.writeText(`${window.location.origin}/character/shared/${char.shared_id}`);
                  setToastTime(common_util.get_time_number());
                } else {
                  navigator.clipboard.writeText(`${window.location.origin}/character/shared/${char.shared_id}`);
                  setToastTime(common_util.get_time_number());
                }
              } catch (ex) {
                console.error(ex);
              }
            }}
          />
        </FlexBox>
      </>
    );
  } 

  const renderCopyButton = () => {
    return (
      <>
        <FlexBox className="centered">
          <CopyButton
            no_fab
            // log_it
            disabled={ processing || !api.online } 
            onClick={copyCharacter}
          />
        </FlexBox>
      </>
    );
  } 

  const renderArchiveButton = () => {
    return (
      <>
        <FlexBox className="centered">
          <ArchiveButton
            no_fab
            // log_it
            is_archived={char.is_archived}
            disabled={ processing || !api.online } 
            onClick={ async () => {
              await updateCharacter({ is_archived: !char.is_archived });
              onUpdated();
              setPing(!ping);
            }}
          />
        </FlexBox>
      </>
    );
  } 

  const renderDeleteButton = () => {
    return (
      <>
        <FlexBox className="centered">
          <DeleteButton
            no_fab
            obj_name={char.name}
            disabled={ processing || !api.online } 
            onClick={deleteCharacter}
          />
        </FlexBox>
      </>
    );
  } 

  const renderControls = () => {
    return (
      <FlexBox direction="column">
        <FlexBox direction="row">
          { renderViewButton() }
          { renderShareButton() }
          { renderCopyButton() }
          { renderArchiveButton() }
          { renderDeleteButton() }
        </FlexBox>
      </FlexBox>
    );
  }

  const renderMainDetails = () => {
    return (
      <FlexBox direction="row">
        <FlexBox className="padded">
          <DisplayObject obj={char} />
        </FlexBox>
        { char.image_url !== "" &&
          <FlexBox className="padded">
            <div style={{ float: "right" }}>
              <img src={ char.image_url } alt="character image" 
                style={{
                  maxWidth: "35px",
                  maxHeight: "35px",
                }}
              />
            </div>
          </FlexBox>
        }
      </FlexBox>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <div className="padded">
        <FlexBox direction="column" className={cx(classes.root)}>
          { renderMainDetails() }
          { renderControls() }
        </FlexBox>
        <ToastBox
          openTime={ toast_time }
        >
          Share Link copied to Clipboard
        </ToastBox>
        { renderExtras() }
      </div>
    );
  }
}
